import { type CSSProperties, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { dividerHorizontal, dividerVertical } from './divider.css';

export interface DividerProps {
  orientation?: 'horizontal' | 'vertical';
  /** Used in flex containers to allow vertical divider to stretch */
  flexItem?: boolean;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
}

export function Divider({
  orientation = 'horizontal',
  flexItem = false,
  children,
  style,
  className,
}: DividerProps) {
  const isVertical = orientation === 'vertical';

  if (children) {
    return (
      <div
        role="separator"
        style={{ display: 'flex', alignItems: 'center', gap: 8, ...style }}
        className={className}
      >
        <hr aria-hidden="true" className={dividerHorizontal} style={{ flex: 1, margin: 0 }} />
        {children}
        <hr aria-hidden="true" className={dividerHorizontal} style={{ flex: 1, margin: 0 }} />
      </div>
    );
  }

  // F16 (secondary): surface `flexItem` so callers (and CSS) can react to it.
  // A vertical divider inside a flex parent needs `align-self: stretch` to
  // fill the row height; consumers can target `hr[data-flex-item="true"]`.
  return (
    <hr
      role="separator"
      data-flex-item={flexItem ? 'true' : undefined}
      className={clsx(isVertical ? dividerVertical : dividerHorizontal, className)}
      style={flexItem ? { alignSelf: 'stretch', ...style } : style}
    />
  );
}
